import { API_BASE_URL, api } from "./api";

interface StreamEvent {
  id: number;
  ts: number;
  level: "info" | "warn" | "error";
  msg: string;
  data?: Record<string, unknown>;
}

const MAX_HISTORY = 100;
let stopped = false;
let es: EventSource | null = null;
let consoleBuffer: string[] = [];

export function startLogStream(): () => void {
  if (typeof EventSource === "undefined") {
    return () => undefined;
  }
  stopped = false;

  function connect() {
    if (stopped) return;
    const url = streamUrl();
    es = new EventSource(url, { withCredentials: true });

    es.addEventListener("open", () => {
      console.info("[flowgen] connected to backend log stream");
    });

    es.addEventListener("log", (e) => {
      try {
        const ev: StreamEvent = JSON.parse((e as MessageEvent).data);
        write(ev);
      } catch {
        /* ignore malformed */
      }
    });

    es.addEventListener("error", () => {
      if (stopped) return;
      // Browser will auto-reconnect; just log once per cycle.
      console.warn("[flowgen] log stream disconnected, retrying...");
    });
  }

  connect();
  return () => {
    stopped = true;
    if (es) {
      es.close();
      es = null;
    }
  };
}

function streamUrl(): string {
  const base = API_BASE_URL.replace(/\/+$/, "");
  const token = api.getToken();
  const path = base.endsWith("/api") ? `${base}/logs/stream` : `${base}/api/logs/stream`;
  if (!token) return path;
  return `${path}?token=${encodeURIComponent(token)}`;
}

function write(ev: StreamEvent) {
  const time = new Date(ev.ts).toISOString().slice(11, 23);
  const tag = ev.level.toUpperCase().padEnd(5);
  const bits: string[] = [];
  const d = ev.data ?? {};
  if (typeof d.sceneId === "string") bits.push(`scene=${d.sceneId}`);
  if (typeof d.sceneTitle === "string") bits.push(`"${d.sceneTitle}"`);
  if (typeof d.model === "string") bits.push(`model=${d.model}`);
  if (typeof d.tookMs === "number") bits.push(`took=${d.tookMs}ms`);
  if (typeof d.videoUrlHost === "string") bits.push(`host=${d.videoUrlHost}`);
  if (typeof d.refImageCount === "number") bits.push(`refs=${d.refImageCount}`);
  if (typeof d.method === "string" && typeof d.url === "string") {
    if (typeof d.status === "number") bits.push(`${d.method} ${d.url} -> ${d.status}`);
    else bits.push(`${d.method} ${d.url}`);
  }

  const linePrefix = `[flowgen ${time}] ${tag} ${ev.msg}${bits.length ? "  " + bits.join(" ") : ""}`;

  // Errors: surface the full err payload on its own line so multi-line
  // OpenRouter responses stay readable in DevTools.
  if (typeof d.err === "string" && d.err.length > 0) {
    const line = `${linePrefix}\n        ${d.err.replace(/\n/g, "\n        ")}`;
    consoleBuffer.push(line);
    if (consoleBuffer.length > MAX_HISTORY) consoleBuffer.shift();
    if (ev.level === "error") console.error(line);
    else if (ev.level === "warn") console.warn(line);
    else console.log(line);
    return;
  }

  consoleBuffer.push(linePrefix);
  if (consoleBuffer.length > MAX_HISTORY) consoleBuffer.shift();
  if (ev.level === "error") console.error(linePrefix);
  else if (ev.level === "warn") console.warn(linePrefix);
  else console.log(linePrefix);
}

export function _debugGetBuffer(): string[] {
  return [...consoleBuffer];
}
